import { useMemo, useState } from "react";
import { Percent } from "lucide-react";
import { ToolLayout, TwoCol } from "@/components/tools/ToolLayout";
import { SectionCard, Field, MoneyInput, SliderRow, Segmented, StatCard, InfoRow, compactINR } from "@/components/tools/kit";
import { capitalGains } from "@/lib/calculators";

const fyString = (start) => `${start}-${String((start + 1) % 100).padStart(2, "0")}`;

export default function CapitalGains() {
  const [assetType, setAssetType] = useState("equity");
  const [term, setTerm] = useState("long");
  const [buyValue, setBuy] = useState(1000000);
  const [sellValue, setSell] = useState(1800000);
  const [expenses, setExpenses] = useState(0);
  const [purchaseYear, setPurchaseYear] = useState(2020);
  const [slabRate, setSlabRate] = useState(30);

  const purchaseFY = fyString(purchaseYear);
  const r = useMemo(
    () => capitalGains({ assetType, buyValue, sellValue, expenses, term, purchaseFY, slabRate }),
    [assetType, buyValue, sellValue, expenses, term, purchaseFY, slabRate]
  );

  const longLabel = assetType === "equity" ? "Long term (held > 12 months)" : "Long term (held > 24 months)";
  const shortLabel = assetType === "equity" ? "Short term (≤ 12 months)" : "Short term (≤ 24 months)";

  return (
    <ToolLayout title="Capital Gains Tax" subtitle="Equity & property LTCG / STCG under FY 2025-26 rules" icon={Percent} accent="#D96B43">
      <TwoCol
        left={
          <SectionCard title="Your transaction">
            <div className="space-y-5">
              <Field label="Asset type">
                <Segmented testid="cg-asset" value={assetType} onChange={setAssetType}
                  options={[{ value: "equity", label: "Equity / Equity MF" }, { value: "property", label: "Property / Other" }]} />
              </Field>
              <Field label="Holding period">
                <Segmented testid="cg-term" value={term} onChange={setTerm}
                  options={[{ value: "long", label: "Long term" }, { value: "short", label: "Short term" }]} />
              </Field>
              <p className="text-xs text-[#8A9790] -mt-2">{term === "long" ? longLabel : shortLabel}</p>

              <Field label="Purchase price"><MoneyInput testid="cg-buy" value={buyValue} onChange={setBuy} helper={compactINR(buyValue)} /></Field>
              <Field label="Sale price"><MoneyInput testid="cg-sell" value={sellValue} onChange={setSell} helper={compactINR(sellValue)} /></Field>
              <Field label="Transfer / improvement expenses"><MoneyInput testid="cg-expenses" value={expenses} onChange={setExpenses} helper={compactINR(expenses)} /></Field>

              {assetType === "property" && term === "long" && (
                <Field label="Year of purchase" hint={purchaseFY}>
                  <SliderRow testid="cg-purchase-year" value={purchaseYear} min={2001} max={2024} onChange={setPurchaseYear} />
                </Field>
              )}
              {assetType === "property" && term === "short" && (
                <Field label="Your income-tax slab" hint={slabRate + "%"}>
                  <SliderRow testid="cg-slab" value={slabRate} min={5} max={30} step={5} suffix="%" onChange={setSlabRate} />
                </Field>
              )}
            </div>
          </SectionCard>
        }
        right={
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <StatCard testid="cg-gain" label="Capital gain" value={compactINR(r.gain)} accent="#2A6F7F" />
              <StatCard testid="cg-tax" label="Tax payable" value={compactINR(r.total)} accent="#D96B43" big />
              <StatCard testid="cg-net" label="Net profit after tax" value={compactINR(r.netProfit)} accent="#2D6A4F" />
            </div>
            <SectionCard title="How it's taxed">
              <div className="mb-3 rounded-xl bg-[#FBF9F5] border border-[#EFE9DD] px-4 py-3">
                <p className="text-sm font-heading font-bold text-[#1A231F]" data-testid="cg-method">{r.method}</p>
                <p className="text-xs text-[#8A9790] mt-0.5">Effective rate {r.taxRate}% + 4% health & education cess</p>
              </div>
              <InfoRow label="Gross capital gain" value={compactINR(r.gain)} />
              {r.exemption > 0 && <InfoRow label="Exemption (Sec 112A)" value={"– " + compactINR(r.exemption)} />}
              {r.indexedCost > 0 && <InfoRow label="Indexed cost of acquisition" value={compactINR(r.indexedCost)} />}
              <InfoRow label="Taxable gain" value={compactINR(r.taxableGain)} strong />
              <InfoRow label={`Tax @ ${r.taxRate}%`} value={compactINR(r.tax)} />
              <InfoRow label="Cess (4%)" value={compactINR(r.cess)} />
              <InfoRow label="Total tax payable" value={compactINR(r.total)} strong />
            </SectionCard>
            <SectionCard title="Good to know">
              <ul className="text-sm text-[#5C6A64] space-y-2 list-disc pl-5">
                <li>Equity LTCG enjoys a <strong>₹1.25 lakh</strong> annual exemption; only the excess is taxed at 12.5%.</li>
                <li>For property bought before 23-Jul-2024, we auto-pick the lower of <strong>12.5% without indexation</strong> or <strong>20% with indexation</strong>.</li>
                <li>Short-term property/other gains are added to income and taxed at your slab.</li>
              </ul>
            </SectionCard>
          </>
        }
      />
    </ToolLayout>
  );
}
